import { CartDto, CartItemDto } from '@mkp/publication/models';
import { Product } from '@mkp/shared/models';
import { formatPrice, getCartItemLongName } from '@mkp/shared/util-product';
import {
  EcommerceActionType,
  EcommerceCategory,
  EcommerceCategoryAccumulator,
  EcommercePlatformType,
  EventFunnelType,
} from '@mkp/tracking/models';
import { TranslateService } from '@ngx-translate/core';

export type EcommercePayload =
  | { cart: CartDto; funnelName: EventFunnelType; action?: EcommerceActionType }
  | {
      products: Product[];
      funnelName: EventFunnelType;
      platform?: EcommercePlatformType;
      action?: EcommerceActionType;
    };

export const getEcommerceCategory = (
  source: EcommercePayload,
  translateService: TranslateService
): EcommerceCategory =>
  'cart' in source
    ? getEcommerceCategoryFromCart(source.cart, source.funnelName, source.action, translateService)
    : getEcommerceCategoryFromProducts(
        source.products,
        source.funnelName,
        source.platform,
        source.action,
        translateService
      );

export const getEcommerceCategoryFromCart = (
  cart: CartDto,
  funnelName: EventFunnelType,
  ecommerceAction: EcommerceActionType | undefined,
  translateService: TranslateService
): EcommerceCategory => ({
  ecommerce_action: ecommerceAction,
  ecommerce_checkout_funnel_name: funnelName,
  ...reduceCartItems(cart.items, translateService),
  ecommerce_transaction_id: cart.tokenValue,
  ecommerce_transaction_total: formatPrice(cart.total),
});

// Note: Deprecated products are filtered at the API level (ProductResource)
export const getEcommerceCategoryFromProducts = (
  products: Product[],
  funnelName: EventFunnelType,
  platform: EcommercePlatformType | undefined,
  ecommerceAction: EcommerceActionType | undefined,
  translateService: TranslateService
): EcommerceCategory => {
  const productDetails = <T = string>(
    prop: keyof Product,
    mapFunc: CallableFunction | undefined = undefined
  ): T[] =>
    products.map((product: Product) => (mapFunc ? mapFunc(product[prop]) : (product[prop] as T)));

  return {
    ecommerce_action: ecommerceAction,
    ecommerce_checkout_funnel_name: funnelName,
    ecommerce_product_id: productDetails('code'),
    ecommerce_product_list_name: platform,
    ecommerce_product_name: productDetails('longname', (x: string) => translateService.instant(x)),
    ecommerce_product_price: products.map((product) => product.basePrice),
    ecommerce_product_quantity: products.map(() => 1),
  };
};

const reduceCartItems = (
  items: CartItemDto[],
  translateService: TranslateService
): EcommerceCategoryAccumulator => {
  const initialValue = {
    ecommerce_order_id: [],
    ecommerce_product_id: [],
    ecommerce_product_name: [],
    ecommerce_product_paid_free: [],
    ecommerce_product_price: [],
    ecommerce_product_quantity: [],
    ecommerce_unit_price: [],
  };

  return items.reduce<EcommerceCategoryAccumulator>((acc, item) => {
    const { variant, subtotal, quantity, unitPrice } = item;
    acc.ecommerce_order_id.push(item.id.toString());
    acc.ecommerce_product_id.push(variant.product.code);
    acc.ecommerce_product_name.push(translateService.instant(getCartItemLongName(item)));
    acc.ecommerce_product_paid_free.push(subtotal ? 'Paid' : 'Free');
    acc.ecommerce_product_price.push(formatPrice(subtotal));
    acc.ecommerce_product_quantity.push(quantity);
    acc.ecommerce_unit_price.push(formatPrice(unitPrice));
    return acc;
  }, initialValue);
};
